import { useMemo, useState } from 'react'
import { Button, Input } from '@heroui/react'
import { putQueue, queueUses } from '../../lib/profilesDoc'
import { nameIn } from '../../lib/staffText'
import type { OperationDoc, ProfileProblem, ProfileQueue, QueueType } from '../../lib/types'
import { useStaffText } from '../../ui/StaffText'
import { NamesField, codeTyped } from '../recordParts'
import { Confirm } from '../sectionKit'
import { NumInput, Segmented, cardProblems, useLiveProblems, useProblemSlots } from './sheetParts'
import { InnerPage, type Crumb } from '../settingsKit'

const CODE_MAX = 16
const LETTER_MAX = 4

export function QueueSheet({
  doc,
  queue,
  problems,
  readOnly,
  onApply,
  onRemove,
  onClose,
  trail,
}: {
  doc: OperationDoc
  queue: ProfileQueue
  problems: ProfileProblem[]
  readOnly: boolean
  onApply: (q: ProfileQueue) => void
  onRemove: () => void
  onClose: () => void
  /** The path back: the profiles list, the profile, and the group of a segment opened from it. */
  trail: Crumb[]
}) {
  const { lang, say } = useStaffText()
  const [draft, setDraft] = useState<ProfileQueue>(queue)
  const set = (patch: Partial<ProfileQueue>) => setDraft((d) => ({ ...d, ...patch }))

  const trial = useMemo(() => putQueue(doc, draft), [doc, draft])
  const live = useLiveProblems(trial)
  const slots = useProblemSlots(cardProblems(live, 'queue', queue.id, problems), [
    'name', 'code', 'letter', 'firstNumber', 'lastNumber', 'serviceLevelSec', 'type',
  ])

  // What routes here keeps the queue; read from the page's document, not the draft.
  const uses = queueUses(doc, queue.id)
  const useText = uses.map((u) => {
    const g = doc.groups.find((x) => x.id === u.groupId)
    const group = g?.name.trim() || say('profiles.group')
    if (u.kind === 'default') return say('profiles.qs.defaultOf', { group })
    const s = g?.segments.find((x) => x.id === u.segmentId)
    return say('profiles.qs.segmentIn', { segment: s?.name.trim() || say('profiles.segment'), group })
  })

  const types: ReadonlyArray<readonly [QueueType, string]> = [
    ['normal', say('profiles.qs.normal')],
    ['appointment', say('profiles.qs.booked')],
  ]

  return (
    <InnerPage
      before={trail}
      title={nameIn(queue.name, lang, queue.code) || say('profiles.queue')}
      mark={<span className="stub stub--lg" aria-hidden="true">{draft.letter || '?'}</span>}
      onClose={onClose}
      onSubmit={() => onApply(draft)}
      submitLabel={say('profiles.sheet.done')}
      hideSubmit={readOnly}
      asForm={false}
      extra={
        readOnly ? null : uses.length > 0 ? (
          <Button variant="danger-soft" isDisabled>
            {say('profiles.qs.remove')}
          </Button>
        ) : (
          <Confirm
            ask={say('profiles.qs.remove')}
            question={say('profiles.qs.removeAsk')}
            yes={say('profiles.sheet.remove')}
            keep={say('profiles.sheet.keep')}
            onYes={onRemove}
          />
        )
      }
    >
      {slots.head}
      <fieldset className="pf-sheet" disabled={readOnly}>
        <NamesField legend={say('profiles.sheet.name')} value={draft.name} onChange={(name) => set({ name })} />
        {slots.at('name')}

        <div className="pf-sheet__pair">
          <label className="field">
            <span>{say('profiles.qs.code')}</span>
            <Input
              className="pf-code"
              value={draft.code}
              maxLength={CODE_MAX}
              autoCapitalize="characters"
              spellCheck={false}
              aria-invalid={slots.has('code') || undefined}
              onChange={(e) => set({ code: codeTyped(e.target.value).slice(0, CODE_MAX) })}
            />
            {slots.at('code')}
          </label>
          <label className="field">
            <span>{say('profiles.qs.letter')}</span>
            <Input
              className="pf-code"
              value={draft.letter}
              maxLength={LETTER_MAX}
              autoCapitalize="characters"
              spellCheck={false}
              aria-invalid={slots.has('letter') || undefined}
              onChange={(e) => set({ letter: e.target.value.toUpperCase().slice(0, LETTER_MAX) })}
            />
            {slots.at('letter')}
          </label>
        </div>

        <div className="pf-sheet__pair">
          <label className="field">
            <span>{say('profiles.qs.first')}</span>
            <NumInput
              value={draft.firstNumber}
              min={1}
              max={9999}
              invalid={slots.has('firstNumber')}
              onChange={(n) => set({ firstNumber: n ?? 0 })}
            />
            {slots.at('firstNumber')}
          </label>
          <label className="field">
            <span>{say('profiles.qs.last')}</span>
            <NumInput
              value={draft.lastNumber}
              min={1}
              max={9999}
              invalid={slots.has('lastNumber')}
              onChange={(n) => set({ lastNumber: n ?? 0 })}
            />
            {slots.at('lastNumber')}
          </label>
        </div>

        <label className="field">
          <span>{say('profiles.qs.target')}</span>
          <NumInput
            value={draft.serviceLevelSec / 60}
            min={1}
            max={240}
            invalid={slots.has('serviceLevelSec')}
            onChange={(n) => set({ serviceLevelSec: Math.round((n ?? 0) * 60) })}
          />
        </label>
        {slots.at('serviceLevelSec')}

        <fieldset className="field">
          <legend>{say('profiles.qs.type')}</legend>
          <Segmented label={say('profiles.qs.type')} options={types} value={draft.type} disabled={readOnly} onChange={(type) => set({ type })} />
        </fieldset>
        {slots.at('type')}
      </fieldset>

      {uses.length > 0 && !readOnly && (
        <section className="field">
          <span className="pf-sheet__label">{say('profiles.qs.usedBy')}</span>
          <ul className="pf-uses">
            {useText.map((text, i) => (
              <li key={i}>{text}</li>
            ))}
          </ul>
        </section>
      )}
    </InnerPage>
  )
}
